import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Card, Col, Drawer, Empty, Row, Spin, Table, Tag, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Activity, Clock, FileSearch, MapPin, User } from 'lucide-react';
import { ensureDataUrl } from '../../utils/helpers';
import {
  extractTaskQuery,
  getTaskSearchLabel,
  getTaskStatusMeta,
} from '../../utils/taskDisplayHelpers';
import './adminLayout.sass';

const { Text, Paragraph, Title } = Typography;

const formatDt = (v) => (v ? new Date(v).toLocaleString('ru-RU') : '—');
const formatDate = (v) => (v ? new Date(v).toLocaleDateString('ru-RU') : '—');

const GENDER_LABELS = {
  male: 'Мужской',
  female: 'Женский',
  other: 'Другое',
};

function roleMeta(role) {
  switch (role) {
    case 'admin':
      return { label: 'Админ', color: 'gold' };
    case 'manager':
      return { label: 'Руководитель', color: 'blue' };
    default:
      return { label: 'Пользователь', color: 'default' };
  }
}

function sessionDurationSec(row) {
  if (!row?.connected_at) return 0;
  const end = row.disconnected_at ? new Date(row.disconnected_at) : new Date();
  return Math.max(0, Math.round((end - new Date(row.connected_at)) / 1000));
}

function formatDurationSec(sec) {
  if (!sec) return '—';
  if (sec < 60) return `${sec} сек`;
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  return `${h} ч ${m % 60} мин`;
}

function ProfileStat({ icon: Icon, label, value, tone = 'blue' }) {
  return (
    <div className={`admin-user-profile__stat admin-user-profile__stat--${tone}`}>
      <div className="admin-user-profile__stat-icon">
        <Icon size={18} />
      </div>
      <div>
        <div className="admin-user-profile__stat-value">{value}</div>
        <div className="admin-user-profile__stat-label">{label}</div>
      </div>
    </div>
  );
}

const UserDetailsDrawer = ({ open, userId, onClose, getUser, isOnline = false }) => {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    if (!open || !userId) {
      setUser(null);
      setSessions([]);
      setTasks([]);
      return;
    }
    setLoading(true);
    getUser(userId, { sessionsLimit: 30, tasksLimit: 20 })
      .then((res) => {
        setUser(res.user);
        setSessions(res.sessions || []);
        setTasks(res.tasks || []);
      })
      .catch(() => {
        setUser(null);
        setSessions([]);
        setTasks([]);
      })
      .finally(() => setLoading(false));
  }, [open, userId, getUser]);

  const role = useMemo(() => roleMeta(user?.role), [user?.role]);
  const avatarSrc = ensureDataUrl(user?.avatar_data_url);
  const displayName = user?.display_name || user?.username || 'Пользователь';

  return (
    <Drawer
      title={null}
      width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 24 : 560)}
      open={open}
      onClose={onClose}
      destroyOnClose
      className="admin-user-profile-drawer"
      styles={{ body: { padding: 0, background: 'var(--theme-content-bg, #f5f5f5)' } }}
    >
      <Spin spinning={loading}>
        {user ? (
          <div className="admin-user-profile">
            <div className="admin-user-profile__hero">
              <Avatar
                size={88}
                src={avatarSrc || undefined}
                icon={!avatarSrc ? <UserOutlined /> : undefined}
                className="admin-user-profile__avatar"
              />
              <div className="admin-user-profile__hero-main">
                <Title level={4} className="admin-user-profile__name">
                  {displayName}
                </Title>
                <Text type="secondary" className="admin-user-profile__username">
                  @{user.username}
                </Text>
                <div className="admin-user-profile__tags">
                  <Tag color={role.color}>{role.label}</Tag>
                  <Tag color={user.is_active !== false ? 'success' : 'error'}>
                    {user.is_active !== false ? 'Активен' : 'Заблокирован'}
                  </Tag>
                  {isOnline && <Tag color="processing">Сейчас в сервисе</Tag>}
                </div>
              </div>
            </div>

            <div className="admin-user-profile__body">
              <Row gutter={[12, 12]} className="admin-user-profile__stats">
                <Col xs={8}>
                  <ProfileStat icon={FileSearch} label="Запросов" value={user.requests_count ?? 0} tone="green" />
                </Col>
                <Col xs={8}>
                  <ProfileStat icon={Activity} label="Задач" value={user.tasks_count ?? tasks.length} tone="blue" />
                </Col>
                <Col xs={8}>
                  <ProfileStat icon={Clock} label="Сессий" value={sessions.length} tone="purple" />
                </Col>
              </Row>

              <Card size="small" title="О пользователе" className="admin-user-profile__card">
                {user.bio ? (
                  <Paragraph className="admin-user-profile__bio">{user.bio}</Paragraph>
                ) : (
                  <Text type="secondary">Описание не заполнено</Text>
                )}
                <div className="admin-user-profile__facts">
                  <div className="admin-user-profile__fact">
                    <MapPin size={15} />
                    <span>{user.location || 'Местоположение не указано'}</span>
                  </div>
                  <div className="admin-user-profile__fact">
                    <User size={15} />
                    <span>{GENDER_LABELS[user.gender] || 'Пол не указан'}</span>
                  </div>
                  <div className="admin-user-profile__fact">
                    <Clock size={15} />
                    <span>Дата рождения: {formatDate(user.birth_date)}</span>
                  </div>
                </div>
              </Card>

              <Card size="small" title="Активность" className="admin-user-profile__card">
                <div className="admin-user-profile__facts">
                  <div className="admin-user-profile__fact">
                    <span>Последний вход: <strong>{formatDt(user.last_login_at)}</strong></span>
                  </div>
                  <div className="admin-user-profile__fact">
                    <span>Профиль создан: {formatDt(user.profile_created_at)}</span>
                  </div>
                  <div className="admin-user-profile__fact">
                    <span>Профиль обновлён: {formatDt(user.profile_updated_at)}</span>
                  </div>
                </div>
              </Card>

              <Card size="small" title="Последние сессии" className="admin-user-profile__card">
                <Table
                  size="small"
                  rowKey="id"
                  pagination={{ pageSize: 5, size: 'small' }}
                  dataSource={sessions}
                  scroll={{ x: 'max-content' }}
                  columns={[
                    { title: 'Подключение', dataIndex: 'connected_at', render: formatDt, width: 150 },
                    { title: 'Отключение', dataIndex: 'disconnected_at', render: formatDt, width: 150 },
                    {
                      title: 'Длительность',
                      width: 100,
                      render: (_, row) => formatDurationSec(sessionDurationSec(row)),
                    },
                    { title: 'IP', dataIndex: 'ip', width: 110, render: (v) => v || '—' },
                  ]}
                />
              </Card>

              <Card size="small" title="Последние поиски" className="admin-user-profile__card">
                {tasks.length ? (
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={{ pageSize: 8, size: 'small', hideOnSinglePage: true }}
                    dataSource={tasks}
                    scroll={{ x: 'max-content' }}
                    columns={[
                      {
                        title: 'Тип',
                        key: 'type',
                        width: 150,
                        ellipsis: true,
                        render: (_, row) => getTaskSearchLabel(row),
                      },
                      {
                        title: 'Запрос',
                        key: 'query',
                        ellipsis: true,
                        render: (_, row) => (
                          <span title={extractTaskQuery(row.description)}>
                            {extractTaskQuery(row.description)}
                          </span>
                        ),
                      },
                      {
                        title: 'Статус',
                        key: 'status',
                        width: 110,
                        render: (_, row) => {
                          const st = getTaskStatusMeta(row);
                          return <Tag color={st.color}>{st.label}</Tag>;
                        },
                      },
                      {
                        title: 'Запросов',
                        dataIndex: 'requests_count',
                        width: 85,
                        align: 'center',
                        render: (v) => v || 0,
                      },
                      {
                        title: 'Создан',
                        dataIndex: 'created_at',
                        width: 140,
                        render: formatDt,
                      },
                    ]}
                  />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Поисков пока нет" />
                )}
              </Card>
            </div>
          </div>
        ) : (
          !loading && (
            <div className="admin-user-profile__empty">
              <Empty description="Не удалось загрузить профиль" />
            </div>
          )
        )}
      </Spin>
    </Drawer>
  );
};

export default UserDetailsDrawer;
